import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Spinner } from 'react-bootstrap'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_ALL_PIPELINES_FULL } from '../../gql/queries/pipelines'
import { GET_ALL_LEAD_SOURCE_CATEGORIES } from '../../gql/queries/leadSourceQuery'
import { GENERATE_PUBLIC_TOKEN } from '../../gql/mutations/publicTokenGenerator'
import { parseId } from '../../helpers'

interface Props {
    show: boolean
    onHide: () => void
}

export const GenerateTokenModal: React.FC<Props> = ({ show, onHide }) => {
    const [stagePipelineId, setStagePipelineId] = useState<string>('')
    const [pipelineId, setPipelineId] = useState<string>('')
    const [leadSourceCategoryId, setLeadSourceCategoryId] = useState<string>('')
    const [leadSourceId, setLeadSourceId] = useState<string>('')
    const [campusId, setCampusId] = useState<string>('1')
    const [generatedToken, setGeneratedToken] = useState<string>('')

    // Queries
    const { data: pipelinesData, loading: loadingPipelines } = useQuery(GET_ALL_PIPELINES_FULL, {
        skip: !show,
    })

    const { data: sourcesData, loading: loadingSources } = useQuery(GET_ALL_LEAD_SOURCE_CATEGORIES, {
        variables: { first: 100, offset: 0 },
        fetchPolicy: 'no-cache',
        skip: !show,
    })

    const [generateToken, { loading: generating }] = useMutation(GENERATE_PUBLIC_TOKEN)

    const pipelines = pipelinesData?.allPipelinesFull?.edges?.map((e: any) => e.node) || []
    const categories = sourcesData?.allCategoryLeadSource?.edges?.map((e: any) => e.node) || []

    // Derived stages based on selected pipeline
    const selectedPipeline = pipelines.find((p: any) => p.id === pipelineId)
    const stages = selectedPipeline?.stagePipelineSet?.edges?.map((e: any) => e.node) || []

    // Derived sources based on selected category
    const selectedCategory = categories.find((c: any) => c.id === leadSourceCategoryId)
    const filteredSources = selectedCategory?.leadSourceSet?.edges?.map((e: any) => e.node).filter((s: any) => s.enabled !== false) || []

    useEffect(() => {
        if (!show) {
            setPipelineId('')
            setStagePipelineId('')
            setLeadSourceCategoryId('')
            setLeadSourceId('')
            setCampusId('1')
            setGeneratedToken('')
        }
    }, [show])

    const handleGenerate = async () => {
        if (!stagePipelineId || !leadSourceId || !campusId) {
            toast.error('Please fill all the required fields')
            return
        }

        try {
            const { data } = await generateToken({
                variables: {
                    leadSourceId: Number(parseId(leadSourceId)),
                    stagePipelineId: Number(parseId(stagePipelineId)),
                    campusId: Number(campusId),
                },
            })

            if (data?.generatePublicToken?.success) {
                setGeneratedToken(data.generatePublicToken.token)
                toast.success('Token generated successfully')
            } else {
                const errorMsg = data?.generatePublicToken?.errors?.[0]?.message || 'Failed to generate token'
                toast.error(errorMsg)
            }
        } catch (e: any) {
            toast.error(e.message || 'An error occurred')
        }
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedToken)
        toast.success('Token copied to clipboard')
    }

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Generate Lovable Campaign Token</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <Form.Label>Pipeline</Form.Label>
                    <Form.Select
                        value={pipelineId}
                        onChange={(e) => {
                            setPipelineId(e.target.value)
                            setStagePipelineId('')
                            setGeneratedToken('')
                        }}
                        disabled={loadingPipelines}
                    >
                        <option value="">Select Pipeline...</option>
                        {pipelines.map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="mb-3">
                    <Form.Label>Stage Pipeline (Required)</Form.Label>
                    <Form.Select
                        value={stagePipelineId}
                        onChange={(e) => {
                            setStagePipelineId(e.target.value)
                            setGeneratedToken('')
                        }}
                        disabled={!pipelineId || stages.length === 0}
                    >
                        <option value="">Select Stage...</option>
                        {stages.map((s: any) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="mb-3">
                    <Form.Label>Lead Source Category (Required)</Form.Label>
                    <Form.Select
                        value={leadSourceCategoryId}
                        onChange={(e) => {
                            setLeadSourceCategoryId(e.target.value)
                            setLeadSourceId('')
                            setGeneratedToken('')
                        }}
                        disabled={loadingSources}
                    >
                        <option value="">Select Category...</option>
                        {categories.map((cat: any) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="mb-3">
                    <Form.Label>Lead Source (Required)</Form.Label>
                    <Form.Select
                        value={leadSourceId}
                        onChange={(e) => {
                            setLeadSourceId(e.target.value)
                            setGeneratedToken('')
                        }}
                        disabled={!leadSourceCategoryId || filteredSources.length === 0}
                    >
                        <option value="">Select Lead Source...</option>
                        {filteredSources.map((s: any) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </Form.Select>
                </div>

                <div className="mb-4">
                    <Form.Label>Campus ID (Optional, Default is 1)</Form.Label>
                    <Form.Control
                        type="number"
                        value={campusId}
                        onChange={(e) => {
                            setCampusId(e.target.value)
                            setGeneratedToken('')
                        }}
                    />
                </div>

                {generatedToken && (
                    <div className="alert alert-success d-flex flex-column align-items-center mb-0">
                        <h6 className="mb-2 w-100 text-center fw-bold">Your Generated Token</h6>
                        <div
                            className="bg-white p-2 border rounded text-break w-100 mb-3"
                            style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '13px', fontFamily: 'monospace' }}
                        >
                            {generatedToken}
                        </div>
                        <Button variant="success" size="sm" onClick={handleCopy} className="w-100 fw-bold">
                            <i className="bi bi-clipboard me-2"></i>Copy Token
                        </Button>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleGenerate}
                    disabled={generating || !stagePipelineId || !leadSourceId}
                >
                    {generating ? <Spinner animation="border" size="sm" /> : 'Generate Token'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
